/* BOJOURNAL — facets.js
   Filtering, sorting and filter removal via the Section Rendering API.
   - Delegated listeners: survive result re-renders and Theme Editor reloads.
   - Changing a filter in the drawer quietly fetches the new result count
     and shows it on the Apply button ("Show 12 works") before applying.
   - Without JavaScript the same controls are ordinary links and GET forms. */
(() => {
  const B = window.Bojournal;
  if (!B || window.__bjFacets) return;
  window.__bjFacets = true;

  let controller;
  let countController;
  let countTimer;

  const rootOf = (el) => (el && el.closest ? el.closest('[data-facets-section]') : null) || document.querySelector('[data-facets-section]');

  const withSection = (url, root) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', root.dataset.sectionId);
    return u;
  };

  const urlFromForm = (form) => {
    const params = new URLSearchParams(new FormData(form));
    [...params.keys()].forEach((key) => {
      if (params.getAll(key).every((v) => v === '')) params.delete(key);
    });
    const action = form.getAttribute('action') || window.location.pathname;
    const query = params.toString();
    return query ? `${action}?${query}` : action;
  };

  const render = async (url, root, push = true) => {
    if (!root) return (window.location.href = url);
    const target = new URL(url, window.location.origin);
    if (controller) controller.abort();
    controller = new AbortController();
    const results = root.querySelector('[data-facets-results]');
    results.setAttribute('aria-busy', 'true');
    results.classList.add('is-loading');
    try {
      const response = await fetch(withSection(target, root), { signal: controller.signal });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-facets-results]');
      if (!fresh) throw new Error('No results');
      const drawer = document.getElementById('FacetDrawer');
      const wasOpen = drawer && drawer.isOpen;
      if (wasOpen) drawer.close({ restoreFocus: false });
      results.innerHTML = fresh.innerHTML;
      if (push) window.history.pushState({ facets: true }, '', target.pathname + target.search);
      B.reveal(results);
      const count = results.querySelector('.toolbar__count');
      if (count) B.announce(count.textContent.trim());
      // Focus lands on the results, never lost at the top of the document.
      const focus = results.querySelector('[data-facets-open]') || results.querySelector('.toolbar__count');
      if (focus) {
        if (!focus.matches('button, a')) focus.setAttribute('tabindex', '-1');
        focus.focus({ preventScroll: true });
      }
      const top = root.querySelector('[data-toolbar]');
      if (top && top.getBoundingClientRect().top < 0) {
        top.scrollIntoView({ block: 'start', behavior: B.reduceMotion.matches ? 'auto' : 'smooth' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = target.href;
    } finally {
      results.removeAttribute('aria-busy');
      results.classList.remove('is-loading');
    }
  };

  /* Live count for the Apply button while choosing filters. */
  const previewCount = (form) => {
    const root = rootOf(form);
    const button = form.querySelector('[data-facets-apply] .btn__label');
    if (!root || !button) return;
    clearTimeout(countTimer);
    countTimer = setTimeout(async () => {
      if (countController) countController.abort();
      countController = new AbortController();
      button.parentElement.classList.add('is-loading');
      try {
        const response = await fetch(withSection(urlFromForm(form), root), { signal: countController.signal });
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('[data-facets-apply] .btn__label');
        if (fresh) button.textContent = fresh.textContent;
      } catch (error) {
        /* keep the previous label */
      } finally {
        button.parentElement.classList.remove('is-loading');
      }
    }, 300);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    event.preventDefault();
    render(urlFromForm(form), rootOf(form));
  });

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-autosubmit-sort]');
    if (sort) {
      render(urlFromForm(sort.form), rootOf(sort));
      return;
    }
    const form = event.target.closest('[data-facets-form]');
    if (form) previewCount(form);
  });

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-facets-open]');
    if (open) {
      const drawer = document.getElementById('FacetDrawer');
      if (drawer && drawer.open) drawer.open(open);
      return;
    }
    const link = event.target.closest('[data-facet-link]');
    if (link && rootOf(link)) {
      event.preventDefault();
      render(link.href, rootOf(link));
    }
  });

  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-facets-section]')) render(window.location.href, rootOf(null), false);
  });

  /* ------------------------------------------------------------------
     Size switch for the gallery grid: large · standard · compact. The
     choice is kept on this device only; without storage it still works
     for the visit.
     ------------------------------------------------------------------ */
  const DENSITY_KEY = 'bj-density';
  const readDensity = () => {
    try {
      return window.localStorage.getItem(DENSITY_KEY) || 'standard';
    } catch (error) {
      return 'standard';
    }
  };

  if (B.Component && B.define && !customElements.get('grid-density')) {
    class GridDensity extends B.Component {
      init() {
        this.root = this.closest('.collection');
        this.buttons = [...this.querySelectorAll('[data-density]')];
        const current = (this.root && this.root.dataset.density) || readDensity();
        this.apply(current, false);
        this.buttons.forEach((button) => {
          this.on(button, 'click', () => this.apply(button.dataset.density, true));
        });
      }

      apply(value, save) {
        const known = this.buttons.some((button) => button.dataset.density === value);
        const density = known ? value : 'standard';
        if (this.root) {
          if (density === 'standard') this.root.removeAttribute('data-density');
          else this.root.dataset.density = density;
        }
        this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.density === density)));
        if (!save) return;
        try {
          window.localStorage.setItem(DENSITY_KEY, density);
        } catch (error) {
          /* storage unavailable: the choice lasts for this page */
        }
      }
    }
    B.define('grid-density', GridDensity);
  }

  /* ------------------------------------------------------------------
     Browse by colour, shape and size: instant, on the works of this page.
     Colour families are read from each painting's photograph (a small
     copy, through a canvas): its plain margin is left out, every pixel is
     sorted into warm, cool, earthy, dark or light, and the painting joins
     each family that covers at least a fifth of it. Results are kept on
     this device, so a painting is read only once. If photographs cannot
     be read (no CORS), the colour row simply stays hidden.
     ------------------------------------------------------------------ */
  const COLOUR_KEY = 'bj-colours-v2';
  const colourCache = (() => {
    try {
      return JSON.parse(window.localStorage.getItem(COLOUR_KEY) || '{}') || {};
    } catch (error) {
      return {};
    }
  })();
  const saveColours = () => {
    try {
      window.localStorage.setItem(COLOUR_KEY, JSON.stringify(colourCache));
    } catch (error) {
      /* storage unavailable: read again next visit */
    }
  };
  const imageKey = (src) => {
    try {
      const url = new URL(src, window.location.href);
      return url.pathname;
    } catch (error) {
      return src;
    }
  };

  const toHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return [h * 60, s, l];
  };

  const family = (r, g, b) => {
    const [h, s, l] = toHsl(r, g, b);
    if (l < 0.2) return 'dark';
    if (l > 0.72 && s < 0.45) return 'light';
    if (s < 0.12) return l < 0.42 ? 'dark' : l > 0.62 ? 'light' : null;
    if (h >= 15 && h < 55 && s < 0.6 && l < 0.62) return 'earthy';
    if (h >= 55 && h < 150) return s < 0.35 ? 'earthy' : 'cool';
    if (h < 55 || h >= 320) return s >= 0.25 ? 'warm' : null;
    return 'cool';
  };

  const readColours = (img) => {
    const size = 72;
    const k = size / Math.max(img.naturalWidth, img.naturalHeight);
    const w = Math.max(1, Math.round(img.naturalWidth * k));
    const h = Math.max(1, Math.round(img.naturalHeight * k));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data; // throws when not readable
    const at = (x, y) => (y * w + x) * 4;
    const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
    const bg = [0, 1, 2].map((c) => corners.reduce((sum, i) => sum + data[i + c], 0) / 4);
    const counts = { warm: 0, cool: 0, earthy: 0, dark: 0, light: 0 };
    let total = 0;
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const i = at(x, y);
        // Leave out the photograph's plain margin.
        if (Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]) < 24) continue;
        const f = family(data[i], data[i + 1], data[i + 2]);
        total += 1;
        if (f) counts[f] += 1;
      }
    }
    if (total < 30) return [];
    const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const tags = ranked.filter(([, n]) => n / total >= 0.2).map(([name]) => name);
    if (!tags.length && ranked[0][1] > 0) tags.push(ranked[0][0]);
    return tags;
  };

  const coloursOf = (card) =>
    new Promise((resolve) => {
      const shown = card.querySelector('.card__img');
      const src = shown && (shown.currentSrc || shown.getAttribute('src') || '');
      if (!src) return resolve(null);
      const key = imageKey(src);
      if (colourCache[key]) return resolve(colourCache[key]);
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        try {
          const tags = readColours(probe);
          colourCache[key] = tags;
          resolve(tags);
        } catch (error) {
          resolve(null);
        }
      };
      probe.onerror = () => resolve(null);
      try {
        const url = new URL(src, window.location.href);
        if (url.searchParams.has('width')) url.searchParams.set('width', '160');
        probe.src = url.toString();
      } catch (error) {
        probe.src = src;
      }
    });

  if (B.Component && B.define && !customElements.get('art-shapes')) {
    class ArtShapes extends B.Component {
      init() {
        this.grid = this.parentElement && this.parentElement.querySelector('.works-grid');
        if (!this.grid) return;
        this.items = [...this.grid.children].filter((li) => li.querySelector('.card'));
        this.cards = this.items.map((li) => li.querySelector('.card'));
        this.state = { colour: '', shape: '', size: '' };
        this.querySelectorAll('[data-group] [data-value]').forEach((chip) => {
          const key = chip.closest('[data-group]').dataset.group;
          this.on(chip, 'click', () => {
            this.state[key] = this.state[key] === chip.dataset.value ? '' : chip.dataset.value;
            this.apply();
          });
        });
        this.count('shape');
        this.count('size');
        this.refresh();
        this.readAll();
      }

      matches(card, key, value) {
        if (!value) return true;
        if (key === 'colour') return (card.dataset.colours || '').split(' ').includes(value);
        return card.dataset[key] === value;
      }

      count(key) {
        const group = this.querySelector('[data-group="' + key + '"]');
        if (!group) return 0;
        let shown = 0;
        group.querySelectorAll('[data-value]').forEach((chip) => {
          const n = this.cards.filter((card) => this.matches(card, key, chip.dataset.value)).length;
          chip.querySelector('[data-count]').textContent = n;
          chip.hidden = n === 0;
          if (n) shown += 1;
        });
        group.hidden = shown < 2;
        return shown;
      }

      refresh() {
        const visible = [...this.querySelectorAll('[data-group]')].some((group) => !group.hidden);
        this.hidden = !visible;
      }

      async readAll() {
        const artworks = this.cards.filter((card) => card.classList.contains('card--artwork'));
        if (artworks.length < 4) return;
        let read = 0;
        const queue = artworks.slice();
        const worker = async () => {
          while (queue.length) {
            const card = queue.shift();
            const tags = await coloursOf(card);
            if (tags) {
              card.dataset.colours = tags.join(' ');
              read += 1;
            }
          }
        };
        await Promise.all([worker(), worker(), worker(), worker()]);
        saveColours();
        if (read < Math.min(4, artworks.length)) return;
        this.count('colour');
        this.refresh();
        // Opened from a link such as ?colour=warm
        const wanted = new URLSearchParams(window.location.search).get('colour');
        if (wanted && ['warm', 'cool', 'earthy', 'dark', 'light'].includes(wanted)) {
          this.state.colour = wanted;
          this.apply();
        }
      }

      apply() {
        let visible = 0;
        this.items.forEach((li, index) => {
          const card = this.cards[index];
          const ok = ['colour', 'shape', 'size'].every((key) => this.matches(card, key, this.state[key]));
          li.hidden = !ok;
          if (ok) {
            visible += 1;
            li.querySelectorAll('[data-reveal]').forEach((node) => node.classList.add('is-in'));
          }
        });
        this.querySelectorAll('[data-group]').forEach((group) => {
          const current = this.state[group.dataset.group];
          group.querySelectorAll('[data-value]').forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.value === current)));
        });
        const status = this.querySelector('[data-shapes-status]');
        const filtering = this.state.colour || this.state.shape || this.state.size;
        status.textContent = filtering ? (this.dataset.status || '').replace('[n]', visible).replace('[total]', this.items.length) : '';
      }
    }
    B.define('art-shapes', ArtShapes);
  }
})();
